import { Link, useRouterState } from '@tanstack/react-router';
import { Settings } from 'lucide-react';
import { useAuthStore } from '../store/auth.ts';
import { AppSwitcher } from './AppSwitcher.tsx';
import { ThemeToggle } from './ThemeToggle.tsx';
import { Icon } from './Icon.tsx';
import { dv } from '../i18n/dv.ts';

// Contextual screen title + whether this screen shows a back button (i.e. it's a
// detail/form screen rather than a primary bottom-nav destination). Mirrors
// WapSign's mobile top app bar.
function screenFor(pathname: string): { title: string; back: boolean } {
  if (pathname === '/') return { title: dv.nav.dashboard, back: false };
  if (pathname === '/meetings') return { title: dv.nav.meetings, back: false };
  if (pathname.startsWith('/meetings/')) return { title: dv.nav.meetings, back: true };
  if (pathname.startsWith('/statistics')) return { title: dv.nav.statistics, back: false };
  if (pathname.startsWith('/thanfeezu')) return { title: dv.nav.thanfeezu, back: false };
  if (pathname.startsWith('/settings')) return { title: dv.nav.settings, back: false };
  if (pathname === '/profile') return { title: dv.nav.profile, back: false };
  if (pathname.startsWith('/admin/orgs/')) return { title: dv.nav.orgs, back: true };
  if (pathname === '/admin/orgs') return { title: dv.nav.orgs, back: false };
  return { title: dv.appName, back: false };
}

// Material 3 mobile top app bar: back/logo, contextual title, then the app
// controls (ecosystem switcher, notifications, theme, admin settings).
export function Header(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { title, back } = screenFor(pathname);

  return (
    <header
      className="md:hidden sticky top-0 z-30 bg-[hsl(var(--chrome))]"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="flex h-14 items-center gap-1 px-1.5">
        {back ? (
          <button
            onClick={() => window.history.back()}
            aria-label="ފަހަތަށް"
            className="grid size-11 place-items-center rounded-full text-foreground active:bg-muted/60"
          >
            {/* RTL: the back arrow points to the inline-start (right). */}
            <Icon name="arrow_forward" size={24} />
          </button>
        ) : (
          <Link to="/" aria-label={dv.nav.dashboard} className="grid size-11 place-items-center">
            <img src="/icons/wapmeet-mark.svg" alt="" className="size-8 object-contain" />
          </Link>
        )}

        <h1 className="flex-1 min-w-0 truncate text-[1.3rem] font-medium text-ink">{title}</h1>

        {user && (
          <div className="flex items-center gap-0.5 shrink-0">
            <AppSwitcher variant="header" />
            <ThemeToggle />
            {user.isOrgAdmin && (
              <Link
                to="/settings"
                aria-label={dv.nav.settings}
                className="grid size-11 place-items-center rounded-full text-muted-foreground active:bg-muted/60"
              >
                <Settings size={18} strokeWidth={1.7} />
              </Link>
            )}
            <button
              onClick={logout}
              aria-label={dv.auth.logout}
              className="grid size-11 place-items-center rounded-full text-muted-foreground active:bg-muted/60"
            >
              <Icon name="logout" size={20} />
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
